import { useCallback, useEffect, useState } from 'react';
import { getDailyTest, submitDailyTestAnswers } from '../api/dailyTest';

/** @typedef {import('../api/dailyTest').DailyTestResponse} DailyTestResponse */

// Module-level so the dashboard card does not refire on every tab switch (it
// remounts each time renderTabContent runs). Cleared after a submit, because
// answering changes what today's test contains.
const inFlight = new Map();

/** Two owed days are two different decks, so the cache key carries the date. */
const cacheKey = (studentId, date, section) => `${studentId}::${date || ''}::${section || ''}`;

/** The trailing arguments actually given, so a bare call stays a bare call. */
const extraArgs = (date, section) => (section ? [date, section] : date ? [date] : []);

function load(studentId, date, section) {
  const key = cacheKey(studentId, date, section);
  if (!inFlight.has(key)) {
    // Called without `date`/`section` when there are none, so existing callers
    // and their argument assertions see the same single-argument call as before.
    const request = getDailyTest(studentId, ...extraArgs(date, section));
    inFlight.set(
      key,
      request.catch((err) => {
        inFlight.delete(key);
        throw err;
      })
    );
  }
  return inFlight.get(key);
}

/**
 * Drop every cached deck for this student — call after submitting answers.
 * All dates, not just the one submitted: clearing one day changes the owed list
 * that every other cached response reports.
 */
export function invalidateDailyTest(studentId) {
  for (const key of [...inFlight.keys()]) {
    if (key.startsWith(`${studentId}::`)) inFlight.delete(key);
  }
}

/**
 * Fetch a student's daily test. A failed fetch is surfaced as `error` rather
 * than thrown: the dashboard card ignores it and stays invisible, the page
 * shows a retry.
 *
 * @param {string|null|undefined} studentId - falsy defers the fetch entirely
 * @param {string} [date] - which owed day to fetch; omitted means the oldest owed
 * @returns {{ loading: boolean, error: Error|null, data: DailyTestResponse|null,
 *             refetch: () => void, submit: (payload: object) => Promise<object> }}
 */
export function useDailyTest(studentId, date, section) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!studentId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    load(studentId, date, section)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [studentId, date, section, attempt]);

  const refetch = useCallback(() => {
    invalidateDailyTest(studentId);
    setAttempt((n) => n + 1);
  }, [studentId]);

  const submit = useCallback(
    (payload) => submitDailyTestAnswers(studentId, payload, ...extraArgs(date, section))
      .finally(() => invalidateDailyTest(studentId)),
    [studentId, date, section]
  );

  return { loading, error, data, refetch, submit };
}
